import { Modal, Tabs, message } from "antd"
import PropTypes from "prop-types"
import React, { useEffect, useState } from "react"
import styled from "styled-components"

import BottomSheet from "../../../../../_shared/components/BottomSheet"
import { useStateValue } from "../../../../../_shared/context/AppStateStore"
import useRestTable from "../../../../../_shared/hooks/usePaginatedRestResource"
import AgentCodesForm from "./AgentCodesForm"
import BioForm from "./BioForm"
import DateChangeForm from "./DateChangeForm"
import DateForm from "./DateForm"
import ImageForm from "./ImageForm"
import LocationForm from "./LocationForm"
import SlotForm from "./SlotForm"
import { computeSlots } from "./util"

const { TabPane } = Tabs

// ----------------------------------------------------------------------------

const defaultFormState = {
    title: "",
    eventType: "",
    description: "",
    summary: "",
    minimumAge: 35,
    maximumAge: 49,
    slots: [],
    days: [],
    url: "",
    detailUrl: "",
    price: "",
    agentReservationCodes: [],
    bookingsOpen: false,
    published: false,
    isPrivate: false,
    autoPublishDate: null,
    locations: [],
}

function EventsBottomSheet(props) {
    // -------------------------------------
    // Props destructuring
    // -------------------------------------

    const { open, onConfirm, onCancel, event } = props

    // -------------------------------------
    // Hooks (e.g. useState, useMemo ...)
    // -------------------------------------

    const [{ token }] = useStateValue()

    const [eventTypes] = useRestTable("/event-types", token)

    const [formState, setFormState] = useState(defaultFormState)

    // Date change data is handled separately to prevent slot regeneration
    const [dateChangeData, setDateChangeData] = useState({
        shouldUpdateDate: false,
        eventStartDate: null,
    })

    // -------------------------------------
    // Effects
    // -------------------------------------

    useEffect(() => {
        if (!open) {
            setDateChangeData({ shouldUpdateDate: false, eventStartDate: null })
            setFormState(defaultFormState)
            return
        }
        if (!event) {
            return
        }

        setFormState({
            id: event?.id,
            title: event?.title,
            summary: event?.summary,
            slots:
                event.slots?.map((s) => ({
                    ...s,
                    eventType: s.eventType?.id || event.eventType?.id,
                })) || [],
            locations: event?.locations ?? [],
            description: event?.description,
            url: event?.url,
            detailUrl: event?.detailUrl,
            duration: event?.duration,
            days: event?.days,
            price: event?.price,
            published: event?.published,
            bookingsOpen: event?.bookingsOpen,
            autoPublishDate: event?.autoPublishDate,
            minimumAge: event?.minimumAge ?? 35,
            maximumAge: event?.maximumAge ?? 49,
            eventType: event?.eventType?.id
                ? event.eventType.id
                : event.eventType,
            private: event?.private,
            agentReservationCodes: event?.agentReservationCodes ?? [],
        })

        //eslint-disable-next-line
    }, [event, open])

    // -------------------------------------
    // Component functions
    // -------------------------------------

    function handleConfirm() {
        function execute() {
            const error = checkEventValid()
            if (error) {
                message.error(error)
                return
            }
            onConfirm &&
                onConfirm({
                    ...formState,
                    id: event?.id,
                    shouldUpdateDate: dateChangeData?.shouldUpdateDate,
                    newStartDate: dateChangeData?.eventStartDate,
                })
        }

        if (dateChangeData?.shouldUpdateDate) {
            Modal.confirm({
                onOk: execute,
                title: "Conferma modifica evento",
                content: confirmMessage,
            })
        } else {
            execute()
        }
    }

    function handleSubmit(e) {
        e.preventDefault()
    }

    function handleValueChange(
        field,
        {
            recomputeSlots = false,
            changedField = false,
            slotIndex = null,
            extraPivots,
        } = {}
    ) {
        return function (value) {
            setFormState((previousValues) => {
                const newValues = { ...previousValues, [field]: value }
                if (recomputeSlots) {
                    const [newSlots, toManuallyReconnect] = computeSlots({
                        changedField,
                        oldDays: previousValues.days,
                        oldLocations: previousValues.locations,
                        oldSlots: previousValues.slots,
                        days: newValues.days,
                        locations: newValues.locations,
                        eventType: newValues.eventType,
                        slots: newValues.slots,
                        eventTypes,
                        slotIndex,
                        extraPivots,
                    })
                    newValues.slots = newSlots
                    newValues.toManuallyReconnect = toManuallyReconnect
                }

                return newValues
            })
        }
    }

    function handleImageUpload(key, image) {
        if (key === "url") {
            if (image) setFormState((prev) => ({ ...prev, url: image }))
        } else {
            if (image) setFormState((prev) => ({ ...prev, detailUrl: image }))
        }
    }

    function handleClose() {
        onCancel && onCancel()
    }

    function checkEventValid() {
        if (!formState.days?.length) {
            return "Si deve inserire almeno una giornata per l'evento."
        }
        if (!formState.url) {
            return "Nessuna immagine caricata per l'evento"
        }
        if (!formState.title || !formState.summary || !formState.description) {
            return "Inserire titolo, descrizione e riassunto."
        }
    }

    // -------------------------------------
    // Component local variables
    // -------------------------------------

    return (
        <BottomSheet
            open={open}
            className={props.className}
            onCancel={handleClose}
            onConfirm={handleConfirm}
            title={event?.id ? "Modifica evento" : "Crea evento"}
            snapPoint={90}
        >
            <form className="form" onSubmit={handleSubmit}>
                <Tabs tabPosition="top">
                    <TabPane tab="Dettagli" key="details">
                        <BioForm
                            values={formState}
                            onValueChange={handleValueChange}
                            eventTypes={eventTypes}
                        />
                    </TabPane>
                    {!event?.id && (
                        <TabPane tab="Data" key="date">
                            <DateForm
                                values={formState}
                                onValueChange={handleValueChange}
                                eventTypes={eventTypes}
                            />
                        </TabPane>
                    )}
                    {event?.id && (
                        <TabPane
                            tab={event?.id ? "Modifica data" : "Data"}
                            key="change-date"
                        >
                            <DateChangeForm
                                values={dateChangeData}
                                onValueChange={setDateChangeData}
                                eventData={formState}
                                isNewEvent={event?.id ? false : true}
                                eventTypes={eventTypes}
                            />
                        </TabPane>
                    )}
                    <TabPane tab="Immagine" key="gallery">
                        <ImageForm
                            values={formState}
                            onUpload={handleImageUpload}
                            eventTypes={eventTypes}
                        />
                    </TabPane>
                    <TabPane tab="Location" key="location">
                        <LocationForm
                            values={formState}
                            onValueChange={handleValueChange}
                            eventTypes={eventTypes}
                            initialValues={event}
                        />
                    </TabPane>
                    <TabPane tab="Link agente" key="agentAccessCodes">
                        <AgentCodesForm
                            values={formState}
                            onValueChange={handleValueChange}
                        ></AgentCodesForm>
                    </TabPane>
                    <TabPane tab="Slot orari" key="slots">
                        <SlotForm
                            values={formState}
                            onValueChange={handleValueChange}
                            eventTypes={eventTypes}
                            setFormState={setFormState}
                        ></SlotForm>
                    </TabPane>
                </Tabs>
            </form>
        </BottomSheet>
    )
}

// ----------------------------------------------------------------------------
// Component PropTypes and default props
// ----------------------------------------------------------------------------

EventsBottomSheet.propTypes = {
    className: PropTypes.string.isRequired,
    open: PropTypes.bool,
    onConfirm: PropTypes.func,
    onCancel: PropTypes.func,
    event: PropTypes.object,
    reloadEvent: PropTypes.func,
}

EventsBottomSheet.defaultProps = {}

// ----------------------------------------------------------------------------

const StyledEventsBottomSheet = styled(EventsBottomSheet)`
    & {
    }
`
// ----------------------------------------------------------------------------

export default StyledEventsBottomSheet

const confirmMessage =
    "Le date dell'evento hanno subito modifiche. Le prenotazioni verrano spostate sulle giornate corrispondenti e gli utenti prenotati riceveranno un'email di notifica del cambio di data."
